import { useMutation } from 'react-query';
import {
  SignInOutput,
  fetchAuthSession,
  fetchUserAttributes,
  signIn,
} from 'aws-amplify/auth';

import { useClient } from '../../components/hooks';
import { requireMFASetup, requireMFAValidation } from '../../utils/helper';

const [SrpType, PwdType] = [
  process.env.authSRPFlowType,
  process.env.authPWDFlowType,
];

const cognitoLogin = async ({
  username,
  password,
  authFlow = SrpType,
}: {
  username: string;
  password: string;
  authFlow?: string;
}): Promise<SignInOutput> => {
  try {
    const resp = await signIn({
      username,
      password,
      options: {
        authFlowType: authFlow as any,
        ...(authFlow === PwdType ? { um: 'True' } : { um: 'False' }),
      },
    });

    return resp;
  } catch (err: any) {
    if (err?.name === 'NotAuthorizedException' && authFlow === SrpType) {
      const resp: any = await cognitoLogin({
        username,
        password,
        authFlow: PwdType,
      });
      return resp;
    }
    if (err.name === 'UserAlreadyAuthenticatedException') {
      return {
        isSignedIn: true,
        nextStep: {
          signInStep: 'DONE',
        },
      };
    } else {
      throw err;
    }
  }
};

export function useMutateUserSignIn() {
  const { getMfaToken, clientId } = useClient();
  return useMutation(
    async ({
      emailOrUsername,
      password,
    }: {
      emailOrUsername: string;
      password: string;
    }): Promise<[string, string?]> => {
      const session = await fetchAuthSession({ forceRefresh: true });

      if (session.tokens?.accessToken && session.tokens?.idToken) {
        // User is authenticated
        return ['redirect'];
      }

      const signInResult = await cognitoLogin({
        username: emailOrUsername,
        password: password,
      });
      const mfaToken = getMfaToken();
      const attributes = await fetchUserAttributes();

      if (signInResult.nextStep?.signInStep !== 'DONE') {
        return ['challenge', signInResult.nextStep?.signInStep];
      } else if (await requireMFAValidation(mfaToken, clientId))
        return ['validateMfa'];
      else if (requireMFASetup(attributes)) return ['setupMfa'];
      return ['redirect'];
    }
  );
}
